import React, { useState, ReactNode, createContext, useContext, useMemo } from "react"
import { StyledTabsContent, StyledTabsList, StyledTabsTrigger } from "./styles"

// Tabs Root
interface TabsProps {
  defaultValue: string
  children: ReactNode
}

// Tabs Trigger
interface TabsTriggerProps {
  value: string
  children: ReactNode
  className?: string
}

// Tabs Content
interface TabsContentProps {
  value: string
  children: ReactNode
  className?: string
}

// Contexto para Tabs
const TabsContext = createContext<{
  activeTab: string
  setActiveTab: (value: string) => void
}>({
  activeTab: "",
  setActiveTab: () => {},
})


const Tabs = ({ defaultValue, children }: TabsProps) => {
  const [activeTab, setActiveTab] = useState(defaultValue)

  const values = useMemo(() => ({ activeTab, setActiveTab }), [activeTab])
  
  return (
    <TabsContext.Provider value={values}>
      {children}
    </TabsContext.Provider>
  )
}

const TabsList = ({ children, className }: { children: ReactNode; className?: string }) => {
  return <StyledTabsList className={className}>{children}</StyledTabsList>
}


const TabsTrigger = React.forwardRef<HTMLButtonElement, TabsTriggerProps>(
  ({ value, children, className, ...props }, ref) => {
    const { activeTab, setActiveTab } = useContext(TabsContext)
    const isActive = activeTab === value

    return (
      <StyledTabsTrigger
        ref={ref}
        type="button"
        active={isActive}
        className={className}
        onClick={() => setActiveTab(value)}
        {...props}
      >
        {children}
      </StyledTabsTrigger>
    )
  }
)
TabsTrigger.displayName = "TabsTrigger"


const TabsContent = React.forwardRef<HTMLDivElement, TabsContentProps>(
  ({ value, children, className, ...props }, ref) => {
    const { activeTab } = useContext(TabsContext)

    if (activeTab !== value) return null

    return (
      <StyledTabsContent ref={ref} className={className} {...props}>
        {children}
      </StyledTabsContent>
    )
  }
)
TabsContent.displayName = "TabsContent"

export { Tabs, TabsList, TabsTrigger, TabsContent }
